import React, { forwardRef, useRef, useImperativeHandle, useEffect, useState } from 'react'
import {
  FlatList,
  Dimensions,
  View,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
} from 'react-native'
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
  withTiming,
  interpolateColor,
} from 'react-native-reanimated'
import { useTheme } from 'styled-components/native'

import DialogueTouchableOpacity from 'APP/Components/DialogueTouchableOpacity'

interface CarouselDotProps {
  onPress: (index: number) => void
  index?: number
  selected?: boolean
}

export const CarouselDot = ({ onPress, index = 0, selected = false }: CarouselDotProps) => {
  const theme = useTheme()
  const carouselDot = {
    size: 8,
    borderRadius: 4,
    margin: 6,
  }
  const welcomeScreenSlideInvertedDot = theme?.colors?.welcomeScreenSlideInvertedDot
  const welcomeScreenSlideCurrentDot = theme?.colors?.welcomeScreenSlideCurrentDot

  const animatedWidth = useSharedValue(selected ? carouselDot.size * 2 : carouselDot.size)
  const animatedColor = useSharedValue(selected ? 1 : 0)

  useEffect(() => {
    animatedWidth.value = withSpring(selected ? carouselDot.size * 2 : carouselDot.size, {
      damping: 15,
      stiffness: 150,
    })
    animatedColor.value = withTiming(selected ? 1 : 0, {
      duration: 300,
    })
  }, [selected, carouselDot.size])

  const animatedStyle = useAnimatedStyle(() => {
    const backgroundColor = interpolateColor(
      animatedColor.value,
      [0, 1],
      [welcomeScreenSlideInvertedDot, welcomeScreenSlideCurrentDot]
    )

    return {
      backgroundColor,
      width: animatedWidth.value,
      height: carouselDot.size,
      maxHeight: carouselDot.size,
      borderRadius: carouselDot.borderRadius,
      margin: carouselDot.margin,
    }
  })

  return (
    <DialogueTouchableOpacity
      testID={`CAROUSEL_DOT_${index}`}
      onPress={() => onPress(index)}
      analyticsName={`Carousel dot selected ${index}`}
    >
      <Animated.View style={animatedStyle} />
    </DialogueTouchableOpacity>
  )
}

export interface CarouselRef {
  scrollTo: (params: { index: number; animated?: boolean }) => void
  getCurrentIndex: () => number
}

interface CarouselComponentProps<T = any> {
  loop?: boolean
  width?: number
  height?: number
  data?: T[]
  onProgressChange?: (offsetProgress: number, absoluteProgress: number) => void
  renderItem?: (info: { item: T; index: number }) => React.ReactElement | null
  enabled?: boolean
  autoPlay?: boolean
  scrollAnimationDuration?: number
  autoPlayInterval?: number
  onScrollEnd?: (index: number) => void
}

export const CarouselComponent = forwardRef<CarouselRef, CarouselComponentProps>(
  (
    {
      loop = false,
      width = Dimensions.get('window').width,
      height = Dimensions.get('window').height,
      data = [],
      onProgressChange = () => {},
      renderItem = () => null,
      enabled = true,
      autoPlay = false,
      autoPlayInterval = 5000,
      onScrollEnd = () => {},
    },
    ref
  ) => {
    const flatListRef = useRef<FlatList>(null)
    const [currentIndex, setCurrentIndex] = useState(0)
    const autoPlayTimerRef = useRef<NodeJS.Timeout | null>(null)
    const scrollPositionRef = useRef(0)

    // Web swipe support
    const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null)
    const isSwiping = useRef(false)

    // Expose imperative methods via ref
    useImperativeHandle(ref, () => ({
      scrollTo: ({ index, animated = true }: { index: number; animated?: boolean }) => {
        if (flatListRef.current && index >= 0 && index < data.length) {
          flatListRef.current.scrollToIndex({ index, animated })
          setCurrentIndex(index)
        }
      },
      getCurrentIndex: () => currentIndex,
    }))

    // Auto-play functionality
    useEffect(() => {
      if (autoPlay && data.length > 1 && enabled) {
        autoPlayTimerRef.current = setInterval(() => {
          setCurrentIndex((prevIndex) => {
            let nextIndex: number

            if (loop) {
              nextIndex = (prevIndex + 1) % data.length
            } else if (prevIndex < data.length - 1) {
              nextIndex = prevIndex + 1
            } else {
              nextIndex = 0
            }

            if (flatListRef.current) {
              flatListRef.current.scrollToIndex({ index: nextIndex, animated: true })
            }
            return nextIndex
          })
        }, autoPlayInterval)

        return () => {
          if (autoPlayTimerRef.current) {
            clearInterval(autoPlayTimerRef.current)
          }
        }
      }
    }, [autoPlay, data.length, enabled, autoPlayInterval, loop])

    // Handle scroll events
    const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      if (!width || width === 0) return

      const scrollPosition = event.nativeEvent.contentOffset.x
      scrollPositionRef.current = scrollPosition

      const index = Math.round(scrollPosition / width)
      const offsetProgress = (scrollPosition % width) / width
      const absoluteProgress = scrollPosition / width

      if (index !== currentIndex && index >= 0 && index < data.length) {
        setCurrentIndex(index)
      }

      onProgressChange(offsetProgress, absoluteProgress)
    }

    const handleMomentumScrollEnd = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const scrollPosition = event.nativeEvent.contentOffset.x
      const index = Math.round(scrollPosition / width)

      if (index >= 0 && index < data.length) {
        setCurrentIndex(index)
        onScrollEnd(index)
      }
    }

    const renderCarouselItem = ({ item, index }: { item: any; index: number }) => {
      return <View style={{ width, height }}>{renderItem({ item, index })}</View>
    }

    const getItemLayout = (_: any, index: number) => ({
      length: width,
      offset: width * index,
      index,
    })

    // Web swipe handlers
    const handleSwipeStart = (clientX: number, clientY: number) => {
      if (Platform.OS !== 'web' || !enabled) return

      touchStartRef.current = {
        x: clientX,
        y: clientY,
        time: Date.now(),
      }
      isSwiping.current = false
    }

    const handleSwipeMove = (clientX: number) => {
      if (Platform.OS !== 'web' || !enabled || !touchStartRef.current) return

      const deltaX = Math.abs(clientX - touchStartRef.current.x)

      // Mark as swiping if moved more than 10px
      if (deltaX > 10) {
        isSwiping.current = true
      }
    }

    const handleSwipeEnd = (clientX: number) => {
      if (Platform.OS !== 'web' || !enabled || !touchStartRef.current) return

      const deltaX = clientX - touchStartRef.current.x
      const deltaTime = Date.now() - touchStartRef.current.time
      const velocity = Math.abs(deltaX) / deltaTime

      // Minimum swipe distance (30% of width) or fast swipe (velocity > 0.5)
      const minSwipeDistance = width * 0.3
      const shouldSwipe = Math.abs(deltaX) > minSwipeDistance || velocity > 0.5

      if (shouldSwipe && isSwiping.current) {
        let newIndex = currentIndex

        if (deltaX < 0 && currentIndex < data.length - 1) {
          // Swipe left - go to next slide
          newIndex = currentIndex + 1
        } else if (deltaX > 0 && currentIndex > 0) {
          // Swipe right - go to previous slide
          newIndex = currentIndex - 1
        } else if (loop) {
          // Handle loop
          if (deltaX < 0 && currentIndex === data.length - 1) {
            newIndex = 0
          } else if (deltaX > 0 && currentIndex === 0) {
            newIndex = data.length - 1
          }
        }

        if (newIndex !== currentIndex && flatListRef.current) {
          flatListRef.current.scrollToIndex({ index: newIndex, animated: true })
          setCurrentIndex(newIndex)
          onScrollEnd(newIndex)
        }
      }

      touchStartRef.current = null
      isSwiping.current = false
    }

    const webSwipeHandlers =
      Platform.OS === 'web'
        ? {
            onMouseDown: (e: any) => handleSwipeStart(e.clientX, e.clientY),
            onMouseMove: (e: any) => handleSwipeMove(e.clientX),
            onMouseUp: (e: any) => handleSwipeEnd(e.clientX),
            onMouseLeave: (e: any) => {
              if (touchStartRef.current) {
                handleSwipeEnd(e.clientX)
              }
            },
            onTouchStart: (e: any) => {
              if (e.touches && e.touches[0]) {
                handleSwipeStart(e.touches[0].clientX, e.touches[0].clientY)
              }
            },
            onTouchMove: (e: any) => {
              if (e.touches && e.touches[0]) {
                handleSwipeMove(e.touches[0].clientX)
              }
            },
            onTouchEnd: (e: any) => {
              if (e.changedTouches && e.changedTouches[0]) {
                handleSwipeEnd(e.changedTouches[0].clientX)
              }
            },
          }
        : {}

    return (
      <View testID="CAROUSEL_CONTAINER" {...webSwipeHandlers} style={{ width, height }}>
        <FlatList
          testID="CAROUSEL_FLATLIST"
          ref={flatListRef}
          data={data}
          renderItem={renderCarouselItem}
          horizontal
          pagingEnabled
          showsHorizontalScrollIndicator={false}
          scrollEnabled={enabled}
          onScroll={handleScroll}
          onMomentumScrollEnd={handleMomentumScrollEnd}
          scrollEventThrottle={16}
          getItemLayout={getItemLayout}
          keyExtractor={(_, index) => `CAROUSEL_ITEM_${index}`}
          snapToInterval={width}
          decelerationRate="fast"
        />
      </View>
    )
  }
)
